import { Component, Inject } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";

import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../shared/dialogs/components/message-dialog.component';

import { UserService } from '../../humain/user/services/user.service';
import { UserTLR } from '../../humain/user/entities/user-tlr.entity';

import { TransfertPerimetreOrder } from '../entities/transfert-perimetre-order.entity';
import { SelectionPerimetre } from '../entities/selection-perimetre.entity';
import { TypeResponsabilite } from '../entities/type-responsabilite.entity';
import { PerimetresService } from '../services/perimetres.service';
import { JSR310ConversionService } from '../../../shared/jsr310-localdates/services/jsr310-conversion.service';

@Component({
	selector: 'parametrage-perimetres',
	templateUrl: 'parametrage-perimetres.html',
	styleUrls: ['parametrage-perimetres.css'],
})
export class ParametagePerimetresComponent {
	// Getion des composants
	resetAntenneCommands$ = new Subject<number | null | undefined>();
	disableCandidatSelector: boolean = true;
	getUserKey = (user: UserTLR | null)  => user?.uid;
	getUserLabel = (user: UserTLR | null)  => user ? ('(' + user .role + ') ' + user.firstName + ' ' + user.lastName) : '';
	perimetreDataExtraction$ = new Subject<(p: SelectionPerimetre) => void>();
	validationButtonDisabled$!: Observable<boolean>;
	validation$ = new Subject<void>();
	waitingForServer$ = new BehaviorSubject<boolean>(false);
	candidatSelectorResetCommand = new Subject<void>();
	
	// Donnees contextuelles
	candidats: UserTLR[] = [];

	// Saisies de l'utilisateur
	responsabilite$ = new BehaviorSubject<TypeResponsabilite>('CAC');
	antenneId$ = new BehaviorSubject<number | null>(null);
	responsable$ = new BehaviorSubject<UserTLR | null>(null);
	transfertComplet$ = new BehaviorSubject<boolean>(true);

	title!: string;

	constructor(
		private dialog: MatDialog,

		@Inject(JSR310ConversionService) private dateConversionService: JSR310ConversionService,
		@Inject(PerimetresService) private perimetresService: PerimetresService,
		@Inject(UserService) userService: UserService,
	) {
		this.title = 'Associer un CAC à une antenne';
		let candidats$ = combineLatest([this.responsabilite$, this.antenneId$]).pipe(
			mergeMap(
				([responsabilite, antenneId]) => {
					if (antenneId === null) {
						return of(1).pipe(map(_ => [] as UserTLR[]));
					}
					this.title = 'Associer un ' + responsabilite + ' à une antenne';
					switch(responsabilite) {
						case 'CAC':
							return userService.listActiveCACTLRByAntennes([antenneId]);
						case 'CC':
							return userService.listActiveCCTLRByAntennes([antenneId]);
						default:
							throw new Error();
					}
				}
			)
		);
		
		candidats$.subscribe(
			(candidats) => {
				this.disableCandidatSelector = candidats.length === 0;
				this.candidats = candidats
			}
		);
		
		let baseInputs$ = combineLatest([this.responsabilite$, this.antenneId$, this.responsable$, this.transfertComplet$]);
		
		this.validationButtonDisabled$ = combineLatest([this.waitingForServer$, baseInputs$]).pipe(
			map(
				([waitingForServer, [_responsabilite, antenneId, responsable, _transfertComplet]]) => {
					return waitingForServer || antenneId === null || responsable === null;
				}
			)
		);

		let saveOperationRequest$ = this.validation$.pipe(
			withLatestFrom(baseInputs$),
			map(data => data[1]),
		);
	
		saveOperationRequest$.subscribe(
			([responsabilite, antenneId, responsable, transfertComplet]) => {
				if (!responsable) {
					throw new Error();
				}
				
				if (transfertComplet) {
					let perimetre = {
						antennes: [antenneId],
						parcs: [],
						sousParcs: [],
						ouvrages: [],
					} as SelectionPerimetre;
					this.save(responsabilite, responsable, perimetre);
				} else {
					this.perimetreDataExtraction$.next(
						(p: SelectionPerimetre) => this.save(responsabilite, responsable, p)
					);
				}
			}
		);
	}
	
	private save(responsabilite: TypeResponsabilite, responsable: UserTLR, perimetre: SelectionPerimetre) {
		let now = new Date();
		let timestamp = this.dateConversionService.toMandatoryLocalDateTime(now);
		
		let command = {
			responsabilite: responsabilite,
			responsableId: responsable.id,
			perimetre: perimetre,
			timestamp: timestamp,
		} as TransfertPerimetreOrder;
		
		this.waitingForServer$.next(true);
		this.perimetresService.registerResponsability(command).subscribe(
			() => {
				this.waitingForServer$.next(false);
				alert("Opération réalisée avec succès");
				this.antenneId$.next(null);
				this.resetAntenneCommands$.next(null);
				this.responsable$.next(null);
				this.transfertComplet$.next(true);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer$.next(false);
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la modification de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de modification de l'accès", error);
			}
		);
	}
	
	responsabiliteHasChanged(newResponsabilite: TypeResponsabilite) {
		this.responsabilite$.next(newResponsabilite);
		this.candidatSelectorResetCommand.next();
	}
	
}